import React, { useState, useMemo } from "react";
import {
  Box, Typography, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, Button, TextField, MenuItem, Select, FormControl, InputLabel, Grid, TableSortLabel
} from "@mui/material";
import mockData from "../../mocks/todas_evaluaciones.json";

const scoreToQualitativeMap = {
  5: "1 - Excede ampliamente las expectativas",
  4: "2 - Desempeño por encima del esperado",
  3: "3 - Desempeño esperado",
  2: "4 - Desempeño por debajo del esperado",
  1: "5 - Bastante alejado del esperado"
};

const headCells = [
  { id: 'legajo', numeric: true, disablePadding: false, label: 'Nro Legajo' },
  { id: 'nombre', numeric: false, disablePadding: false, label: 'Nombre' },
  { id: 'apellido', numeric: false, disablePadding: false, label: 'Apellido' },
  { id: 'area', numeric: false, disablePadding: false, label: 'Área' },
  { id: 'lider', numeric: false, disablePadding: false, label: 'Líder' },
  { id: 'puesto', numeric: false, disablePadding: false, label: 'Puesto' },
  { id: 'puntaje', numeric: true, disablePadding: false, label: 'Puntaje' },
  { id: 'puntaje_calibrado', numeric: true, disablePadding: false, label: 'Puntaje Calibrado' },
  { id: 'comentarios_calibracion', numeric: false, disablePadding: false, label: 'Comentarios Calibración'},
  { id: 'actions', numeric: false, disablePadding: false, label: 'Acciones' }
];

export default function CalibrateEvaluations() {
  const [evaluations, setEvaluations] = useState(mockData);
  const [calibratedChanges, setCalibratedChanges] = useState({}); // { legajo: { puntaje_calibrado: newScore, comment: "" } }

  const handleCalibratedScoreChange = (legajo, newScore) => {
    setCalibratedChanges(prev => ({
      ...prev,
      [legajo]: { ...prev[legajo], puntaje_calibrado: newScore }
    }));
  };

  const handleCommentChange = (legajo, newComment) => {
    setCalibratedChanges(prev => ({
      ...prev,
      [legajo]: { ...prev[legajo], comment: newComment }
    }));
  };

  const validateAndApplyChange = (legajo, score, comment) => {
    if (score !== undefined && score !== '' && (!comment || comment.trim() === '')) {
      alert(`El empleado con legajo ${legajo} requiere un comentario para el puntaje calibrado.`);
      return false;
    }
    return true;
  };

  const handleSaveIndividualChange = (evaluation) => {
    const change = calibratedChanges[evaluation.legajo];
    const newScore = change?.puntaje_calibrado !== undefined ? change.puntaje_calibrado : evaluation.puntaje_calibrado;
    const newComment = change?.comment !== undefined ? change.comment : (evaluation.comentarios_calibracion || '');

    if (!validateAndApplyChange(evaluation.legajo, newScore, newComment)) {
      return;
    }

    setEvaluations(prevEvaluations =>
      prevEvaluations.map(ev =>
        ev.legajo === evaluation.legajo
          ? { ...ev, puntaje_calibrado: newScore, comentarios_calibracion: newComment }
          : ev
      )
    );
    setCalibratedChanges(prev => {
      const newState = { ...prev };
      delete newState[evaluation.legajo]; // Clear specific changes after saving
      return newState;
    });
    alert(`Cambios guardados para ${evaluation.nombre} ${evaluation.apellido}!`);
  };

  const handleSaveAllChanges = () => {
    const errors = [];
    const updatedEvaluations = evaluations.map(ev => {
      const change = calibratedChanges[ev.legajo];
      if (change && change.puntaje_calibrado !== undefined && change.puntaje_calibrado !== '' && (!change.comment || change.comment.trim() === '')) {
        errors.push(`${ev.nombre} ${ev.apellido} requiere un comentario para el puntaje calibrado.`);
      }
      return change && change.puntaje_calibrado !== undefined
        ? { ...ev, puntaje_calibrado: change.puntaje_calibrado, comentarios_calibracion: change.comment || '' } // Ensure comment is not undefined
        : ev;
    });

    if (errors.length > 0) {
      alert('Por favor, complete los siguientes campos:\n' + errors.join('\n'));
      return;
    }

    setEvaluations(updatedEvaluations);
    setCalibratedChanges({}); // Clear changes after saving
    alert('Cambios guardados exitosamente!');
    console.log("All changes saved:", updatedEvaluations);
  };

  return (
    <Box p={4}>
      <Typography variant="h4" mb={4}>Calibrar Evaluaciones</Typography>
      <TableContainer component={Paper} sx={{ overflowX: 'auto' }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              {headCells.map((headCell) => (
                <TableCell
                  key={headCell.id}
                  align={headCell.numeric ? 'right' : 'left'}
                  padding={headCell.disablePadding ? 'none' : 'normal'}
                  sx={{ fontWeight: 'bold', color: 'primary.main' }}
                >
                  {headCell.label}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {evaluations.map((ev) => {
              const currentCalibratedScore = calibratedChanges[ev.legajo]?.puntaje_calibrado !== undefined
                ? calibratedChanges[ev.legajo].puntaje_calibrado
                : ''; // Default to blank
              const currentComment = calibratedChanges[ev.legajo]?.comment !== undefined
                ? calibratedChanges[ev.legajo].comment
                : (ev.comentarios_calibracion || ''); // Use existing comment if any

              return (
                <TableRow key={ev.legajo}>
                  <TableCell>{ev.legajo}</TableCell>
                  <TableCell>{ev.nombre}</TableCell>
                  <TableCell>{ev.apellido}</TableCell>
                  <TableCell>{ev.area}</TableCell>
                  <TableCell>{ev.lider}</TableCell>
                  <TableCell>{ev.puesto}</TableCell>
                  <TableCell>{scoreToQualitativeMap[ev.puntaje] || ev.puntaje}</TableCell>
                  <TableCell>
                    <FormControl variant="standard" size="small" sx={{ m: 1, minWidth: 120 }}>
                      <Select
                        value={currentCalibratedScore}
                        onChange={(e) => handleCalibratedScoreChange(ev.legajo, e.target.value)}
                        displayEmpty // Allows displaying empty value
                      >
                        <MenuItem value=""><em>Ninguno</em></MenuItem>
                        {[5, 4, 3, 2, 1].map((score) => (
                          <MenuItem key={score} value={score}>
                            {scoreToQualitativeMap[score]}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </TableCell>
                  <TableCell>
                    <TextField
                      variant="standard"
                      size="small"
                      sx={{ m: 1, minWidth: 200 }}
                      value={currentComment}
                      onChange={(e) => handleCommentChange(ev.legajo, e.target.value)}
                      placeholder="Comentario de calibración"
                    />
                  </TableCell>
                  <TableCell>
                    <Button variant="contained" size="small" onClick={() => handleSaveIndividualChange(ev)}>
                      Guardar
                    </Button>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
      <Box mt={3} display="flex" justifyContent="flex-end">
        <Button variant="contained" color="primary" onClick={handleSaveAllChanges}>
          Guardar Cambios
        </Button>
      </Box>
    </Box>
  );
} 